import { useRef, useCallback, useMemo } from 'react';
import WebViewer from '@pdftron/webviewer';
import { releaseOffscreenPageMemory } from '../utils/releaseOffscreenPageMemory';

/**
 * Custom hook to manage multiple WebViewer instances (one per tab)
 * 
 * Features:
 * - Creates and manages WebViewer instances per tab
 * - Handles CSS-based show/hide for instant tab switching
 * - Pauses/resumes rendering for hidden tabs to save CPU/memory
 * - Proper cleanup when tabs are closed
 */
const useWebViewerInstances = () => {
  // Store WebViewer instances per tab: tabId -> { instance, container, documentData }
  const tabInstancesRef = useRef(new Map());
  
  // Store container DOM elements per tab: tabId -> container element
  const tabContainersRef = useRef(new Map());
  
  // Track aggressive repaint timeouts per tab so we can clear them
  const repaintTimeoutRef = useRef(new Map());

  /**
   * Enhanced rendering pause - uses multiple methods for maximum effectiveness
   */
  const pauseRenderingForTab = useCallback((instance) => {
    if (!instance) return false;
    
    let paused = false;
    
    try {
      // Method 1: UI API (primary method)
      if (instance.UI?.setRenderingPaused) {
        instance.UI.setRenderingPaused(true);
        paused = true;
      }
      
      // Method 2: DocumentViewer API (if available)
      if (instance.Core?.documentViewer?.disableRendering) {
        instance.Core.documentViewer.disableRendering();
        paused = true;
      }
      
      return paused;
    } catch (e) {
      console.warn('Could not pause rendering:', e);
      return false;
    }
  }, []);

  /**
   * Enhanced rendering resume - resumes rendering in reverse order
   * Also forces view refresh to ensure pages are re-rendered (critical for MS Office files)
   */
  const resumeRenderingForTab = useCallback((instance) => {
    if (!instance) return false;
    
    let resumed = false;
    
    try {
      const { documentViewer } = instance.Core || {};
      
      // Resume in reverse order
      if (documentViewer?.enableRendering) {
        documentViewer.enableRendering();
        resumed = true;
      }
      
      if (instance.UI?.setRenderingPaused) {
        instance.UI.setRenderingPaused(false);
        resumed = true;
      }
      
      // Force view refresh after resuming rendering.
      // This is important for MS Office streaming where canvases can be cleared
      // while hidden/paused, leading to white pages when coming back.
      if (documentViewer) {
        try {
          const currentPage =
            typeof documentViewer.getCurrentPage === "function"
              ? documentViewer.getCurrentPage()
              : null;

          // Immediate view update
          if (typeof documentViewer.updateView === "function") {
            documentViewer.updateView();
          }

          // Short delayed re-navigation to the same page + full refresh.
          // This mimics the \"tiny scroll/zoom\" the user reported fixes the issue,
          // but does it programmatically and very quickly.
          // setTimeout(() => {
          //   try {
          //     if (
          //       currentPage &&
          //       currentPage > 0 &&
          //       typeof documentViewer.setCurrentPage === "function"
          //     ) {
          //       documentViewer.setCurrentPage(currentPage);
          //     }

          //     if (typeof documentViewer.refreshAll === "function") {
          //       documentViewer.refreshAll();
          //     }

          //     console.log(
          //       "🔄 [RESUME] Re-set current page and called refreshAll() after resuming rendering"
          //     );
          //   } catch (refreshError) {
          //     console.warn(
          //       "⚠️ Could not re-navigate/refresh after resuming:",
          //       refreshError
          //     );
          //   }
          // }, 50);
        } catch (updateError) {
          console.warn("⚠️ Could not update/refresh view after resuming:", updateError);
        }
      }
      
      return resumed;
    } catch (e) {
      console.warn('Could not resume rendering:', e);
      return false;
    }
  }, []);

  /**
   * Verify if rendering is actually paused
   */
  const isRenderingPaused = useCallback((instance) => {
    if (!instance) return false;
    
    try {
      if (instance.UI?.isRenderingPaused) {
        return instance.UI.isRenderingPaused();
      }
      return false;
    } catch (e) {
      return false;
    }
  }, []);

  /**
   * Initialize WebViewer instance for a tab
   */
  const initializeTabInstance = useCallback(async (tabId, container) => {
    if (!container || !tabId) {
      console.warn(`⚠️ Cannot initialize tab instance: missing container or tabId`);
      return null;
    }
    
    // Check if instance already exists
    if (tabInstancesRef.current.has(tabId)) {
      const existing = tabInstancesRef.current.get(tabId);
      return existing.instance;
    }
    
    try {
      // Create new WebViewer instance.
      //
      // fullAPI is OFF (2026-07-11 site-speed): fullAPI loads the extra
      // PDFNet WASM module, which meaningfully slows every per-tab boot.
      // Nothing in the matter viewer path calls PDFNet APIs — annotations,
      // forms, bookmarks, text highlight, and office viewing are all
      // standard Core features (Inbox and Mail Intake viewers already run
      // fullAPI: false with the same facilities). If a future feature
      // needs PDFNet (client-side redaction, digital-signature
      // verification, doc.getPDFDoc()), flip this back deliberately.
      const instance = await WebViewer({
        path: '/webviewer/lib/',
        workerPath: '/webviewer/lib/core/',
        licenseKey: process.env.REACT_APP_WEBVIEWER_LICENSE_KEY || process.env.REACT_APP_APRYSE_LICENSE,
        fullAPI: false,
        enableWebSocket: false,
        disabledElements: ['errorModal'],
        disableLogs: true,
        css: '/webviewer-custom.css',
      }, container);
      
      // External links (e.g. AI case-review citations) open directly in a
      // new tab — no Apryse "Security Warning" interstitial (Matthew,
      // 2026-07-15). Feature-detected: setOpenURIHandler shipped in
      // WebViewer 10.10+; we pin ^11.11.
      try {
        const { documentViewer } = instance.Core;
        if (typeof documentViewer.setOpenURIHandler === 'function') {
          // API contract: handler receives (uri, isOpenInNewWindow).
          documentViewer.setOpenURIHandler((uri) => {
            if (uri && typeof uri === 'string') {
              window.open(uri, '_blank', 'noopener,noreferrer');
            }
          });
        }
      } catch (linkHandlerError) {
        // Never let link-handler wiring break viewer boot.
      }

      // Store instance
      tabInstancesRef.current.set(tabId, {
        instance,
        container,
        documentData: null,
        stopReleasingPageMemory: releaseOffscreenPageMemory(instance),
      });
      
      return instance;
    } catch (error) {
      console.error(`❌ Failed to create WebViewer instance for tab ${tabId}:`, error);
      return null;
    }
  }, []);

  /**
   * Register container for a tab
   */
  const registerContainer = useCallback((tabId, container) => {
    if (container) {
      tabContainersRef.current.set(tabId, container);
    } else {
      tabContainersRef.current.delete(tabId);
    }
  }, []);

  /**
   * Get instance for a tab
   */
  const getInstance = useCallback((tabId) => {
    const tabInstance = tabInstancesRef.current.get(tabId);
    return tabInstance?.instance || null;
  }, []);

  /**
   * Get container for a tab
   */
  const getContainer = useCallback((tabId) => {
    return tabContainersRef.current.get(tabId) || null;
  }, []);

  /**
   * Switch to a tab - handles CSS show/hide and rendering pause/resume
   * If tabId is "1" or "0" (matter list tabs), hide all WebViewer containers
   * but DO NOT pause rendering to avoid white flashes when returning to the doc tab.
   */
  const switchToTab = useCallback((tabId) => {
    // If switching to matter list tab ("1" or "0"), hide all WebViewer containers
    // but keep rendering active. Pausing rendering here was causing large
    // DOCX pages to show as white for a moment when returning from Tab 1.
    if (tabId === "1" || tabId === "0") {
      tabContainersRef.current.forEach((container, id) => {
        if (container) {
          // Hide using opacity/visibility/z-index only - keep display to prevent layout jumps
          // Don't change display property as it causes layout recalculation and content jumps
          container.style.visibility = 'hidden';
          container.style.opacity = '0';
          container.style.zIndex = '0';
          container.style.pointerEvents = 'none';
        }
      });
      return;
    }
    
    // For document tabs: hide all other tab containers and pause their rendering
    tabContainersRef.current.forEach((container, id) => {
      if (container && id !== tabId) {
        // Hide using opacity/visibility/z-index only - keep display to prevent layout jumps
        // Don't change display property as it causes layout recalculation and content jumps
        container.style.visibility = 'hidden';
        container.style.opacity = '0';
        container.style.zIndex = '0';
        container.style.pointerEvents = 'none';
        
        // Pause rendering for hidden tabs (critical for CPU/memory)
        const tabInstance = tabInstancesRef.current.get(id);
        if (tabInstance?.instance) {
          pauseRenderingForTab(tabInstance.instance);
        }
      }
    });
    
    // Show active tab container and resume rendering
    const activeContainer = tabContainersRef.current.get(tabId);
    if (activeContainer) {
      // Show using opacity/visibility/z-index only - keep display consistent to prevent layout jumps
      // Don't change display property as it causes layout recalculation and content jumps
      activeContainer.style.visibility = 'visible';
      activeContainer.style.opacity = '1';
      activeContainer.style.zIndex = '10'; // Lower z-index so header stays on top
      activeContainer.style.pointerEvents = 'auto';
      
      // Resume rendering for active tab - but delay it slightly to ensure container is visible
      const tabInstance = tabInstancesRef.current.get(tabId);
      if (tabInstance?.instance) {
        const instance = tabInstance.instance;
        const { documentViewer } = instance.Core || {};
        
        // First, ensure container is visible and wait for it to be ready
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          // Resume rendering for this tab
          resumeRenderingForTab(instance);
          
          // AGGRESSIVE: Multiple methods to force repaint for DOCX files
          // This mimics the "scroll/zoom" fix the user reported.
          // We keep at most one pending timeout per tab and clear old ones.
          if (repaintTimeoutRef.current.has(tabId)) {
            clearTimeout(repaintTimeoutRef.current.get(tabId));
          }
          
          const timeoutId = setTimeout(() => {
            try {
              if (!documentViewer) return;
              
              // // Method 1: Try UI API scroll element
              // try {
              //   const uiScrollEl = instance.UI?.getScrollViewElement?.();
              //   if (uiScrollEl) {
              //     const originalTop = uiScrollEl.scrollTop || 0;
              //     uiScrollEl.scrollTop = originalTop + 1;
              //     // Use requestAnimationFrame to ensure scroll happens
              //     requestAnimationFrame(() => {
              //       uiScrollEl.scrollTop = originalTop;
              //     });
              //     console.log("🖱️ [SWITCH] Method 1: UI scroll element nudged");
              //   }
              // } catch (e) {
              //   // Ignore
              // }
              
              // // Method 2: Try documentViewer scroll element
              // try {
              //   const docScrollEl = documentViewer.getScrollViewElement?.();
              //   if (docScrollEl) {
              //     const originalTop = docScrollEl.scrollTop || 0;
              //     docScrollEl.scrollTop = originalTop + 1;
              //     requestAnimationFrame(() => {
              //       docScrollEl.scrollTop = originalTop;
              //     });
              //     console.log("🖱️ [SWITCH] Method 2: DocumentViewer scroll element nudged");
              //   }
              // } catch (e) {
              //   // Ignore
              // }
              
              // Method 3: Tiny zoom change (0.01% change, imperceptible)
              try {
                if (instance.UI?.setZoomLevel) {
                  const currentZoom = instance.UI.getZoomLevel?.() || 1.0;
                  instance.UI.setZoomLevel(currentZoom + 0.0001);
                  requestAnimationFrame(() => {
                    instance.UI.setZoomLevel(currentZoom);
                  });
                  console.log("🖱️ [SWITCH] Method 3: Tiny zoom change applied");
                }
              } catch (e) {
                // Ignore
              }
              
              // Method 4: Force updateView multiple times
              if (documentViewer.updateView) {
                documentViewer.updateView();
                setTimeout(() => {
                  documentViewer.updateView();
                }, 50);
                setTimeout(() => {
                  documentViewer.updateView();
                }, 100);
                console.log("🖱️ [SWITCH] Method 4: Multiple updateView calls");
              }
              
              
              // Once repaint logic has run for this tab instance, clear the stored timeout id
              repaintTimeoutRef.current.delete(tabId);
            } catch (e) {
              console.warn("⚠️ Error in aggressive repaint methods:", e);
            }
          }, 100); // Longer delay to ensure container is fully visible
          
          repaintTimeoutRef.current.set(tabId, timeoutId);
        });
      }
    }
  }, [pauseRenderingForTab, resumeRenderingForTab]);

  /**
   * Make container visible before loading (ensures WebViewer can render properly)
   */
  const makeContainerVisible = useCallback((tabId) => {
    const container = tabContainersRef.current.get(tabId);
    if (!container) return false;
    
    // Hide all other containers first - use opacity/visibility only to prevent layout jumps
    tabContainersRef.current.forEach((otherContainer, otherId) => {
      if (otherContainer && otherId !== tabId) {
        // Don't change display property as it causes layout recalculation and content jumps
        otherContainer.style.visibility = 'hidden';
        otherContainer.style.opacity = '0';
        otherContainer.style.zIndex = '0';
        otherContainer.style.pointerEvents = 'none';
      }
    });
    
    // Show and bring to front - use opacity/visibility only to prevent layout jumps
    // Don't change display property as it causes layout recalculation and content jumps
    container.style.visibility = 'visible';
    container.style.opacity = '1';
    container.style.zIndex = '100';
    container.style.pointerEvents = 'auto';
    
    return true;
  }, []);

  /**
   * Dispose instance for a tab (cleanup when tab is closed)
   */
  const disposeTabInstance = useCallback(async (tabId) => {
    const tabInstance = tabInstancesRef.current.get(tabId);
    if (!tabInstance) return;
    
    try {
      const { instance, container, stopReleasingPageMemory } = tabInstance;
      stopReleasingPageMemory?.();
      
      // Pause rendering first to stop any ongoing operations
      if (instance) {
        pauseRenderingForTab(instance);
      }
      
      // Get document reference before closing
      let doc = null;
      let documentViewer = null;
      if (instance?.Core?.documentViewer) {
        documentViewer = instance.Core.documentViewer;
        doc = documentViewer.getDocument();
      }
      
      // Close document FIRST (this will clean up internal listeners properly)
      // We do this before disposing to let WebViewer handle its own cleanup
      if (documentViewer && doc) {
        try {
          // Close document (closes viewer session, stops rendering, cleans up internal listeners)
          await documentViewer.closeDocument();
        } catch (e) {
          console.warn(`Warning closing document for tab ${tabId}:`, e);
        }
      }
      
      // CRITICAL: Explicitly dispose document to free WASM memory
      // Do this AFTER closeDocument() so WebViewer has cleaned up its listeners
      // closeDocument() keeps WASM heap allocations alive
      // dispose() releases WASM document buffers, clears rendering caches, and frees native memory
      if (doc && doc.dispose && typeof doc.dispose === 'function') {
        try {
          doc.dispose();
          console.log(`✅ Disposed document for tab ${tabId} - WASM memory should be freed`);
        } catch (e) {
          console.warn(`Warning disposing document for tab ${tabId}:`, e);
        }
      }
      
      // Dispose UI LAST - this should clean up all UI-related resources
      // Do this after document is closed and disposed to avoid listener conflicts
      if (instance?.UI && typeof instance.UI.dispose === 'function') {
        try {
          await instance.UI.dispose();
        } catch (e) {
          console.warn(`Warning disposing UI for tab ${tabId}:`, e);
        }
      }
      
      // Aggressively clear container - remove all children and reset styles
      if (container) {
        try {
          // Remove all child nodes
          while (container.firstChild) {
            container.removeChild(container.firstChild);
          }
          // Clear innerHTML as backup
          container.innerHTML = '';
          // Reset all styles
          container.style.cssText = '';
          // Remove any attributes
          container.removeAttribute('style');
        } catch (e) {
          console.warn(`Warning clearing container for tab ${tabId}:`, e);
        }
      }
      
      // Clear any pending repaint timeout for this tab
      if (repaintTimeoutRef.current.has(tabId)) {
        clearTimeout(repaintTimeoutRef.current.get(tabId));
        repaintTimeoutRef.current.delete(tabId);
      }
      
      // Remove from maps
      tabInstancesRef.current.delete(tabId);
      tabContainersRef.current.delete(tabId);
      
      console.log(`✅ Disposed instance for tab ${tabId}`);
    } catch (error) {
      console.error(`❌ Error disposing instance for tab ${tabId}:`, error);
    }
  }, [pauseRenderingForTab]);

  /**
   * Update document data for a tab
   */
  const updateTabDocumentData = useCallback((tabId, documentData) => {
    const tabInstance = tabInstancesRef.current.get(tabId);
    if (tabInstance) {
      tabInstance.documentData = documentData;
    }
  }, []);

  /**
   * Get document data for a tab
   */
  const getTabDocumentData = useCallback((tabId) => {
    const tabInstance = tabInstancesRef.current.get(tabId);
    return tabInstance?.documentData || null;
  }, []);

  /**
   * Get all active tab IDs
   */
  const getActiveTabIds = useCallback(() => {
    return Array.from(tabInstancesRef.current.keys());
  }, []);

  /**
   * Dispose all instances (for complete cleanup on navigation)
   */
  const disposeAllInstances = useCallback(async () => {
    const allTabIds = Array.from(tabInstancesRef.current.keys());
    console.log(`🧹 [DISPOSE ALL] Disposing ${allTabIds.length} WebViewer instances...`);
    
    // Dispose all instances in parallel for faster cleanup
    const disposePromises = allTabIds.map(tabId => disposeTabInstance(tabId));
    await Promise.allSettled(disposePromises);
    
    // Clear all maps
    tabInstancesRef.current.clear();
    tabContainersRef.current.clear();
    repaintTimeoutRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    repaintTimeoutRef.current.clear();
    
    console.log('✅ [DISPOSE ALL] All instances disposed and maps cleared');
  }, [disposeTabInstance]);

  /**
   * Check if tab has an instance
   */
  const hasInstance = useCallback((tabId) => {
    return tabInstancesRef.current.has(tabId);
  }, []);

  // Memoize the returned API object so consumers can safely include
  // `webViewerInstances` in useEffect/useMemo dependency arrays without
  // triggering a new "identity" every render. All callbacks below are
  // already stable (useCallback), so the memo deps collectively change
  // only when one of those callback identities changes — i.e. when one
  // of their own upstream deps flips.
  return useMemo(
    () => ({
      initializeTabInstance,
      getInstance,
      hasInstance,
      disposeTabInstance,
      disposeAllInstances,

      registerContainer,
      getContainer,
      makeContainerVisible,

      switchToTab,

      updateTabDocumentData,
      getTabDocumentData,

      pauseRenderingForTab,
      resumeRenderingForTab,
      isRenderingPaused,

      getActiveTabIds,
    }),
    [
      initializeTabInstance,
      getInstance,
      hasInstance,
      disposeTabInstance,
      disposeAllInstances,
      registerContainer,
      getContainer,
      makeContainerVisible,
      switchToTab,
      updateTabDocumentData,
      getTabDocumentData,
      pauseRenderingForTab,
      resumeRenderingForTab,
      isRenderingPaused,
      getActiveTabIds,
    ]
  );
};

export default useWebViewerInstances;
